/**
 * 메뉴 권한 게이트(FE UX 보조) — 설정>메뉴 권한(담당자별)을 사이드바·버튼 노출에 반영.
 *
 * 신뢰 경계는 서버(아이템 CRUD 게이트 PermissionCheckService)다. 이 모듈은 그와 **정확히 동일한 규칙**으로 UI를 게이팅해
 * 사용자가 곧바로 403을 마주치지 않도록 한다(하드코딩 분기 아님 — module_code→menuKey 매핑만).
 *
 * 판정 규칙(BE PermissionCheckService 와 일치):
 *   1) 관리자(CompanyAdmin/SuperAdmin) → 항상 허용.
 *   2) 저장된 menu 권한 행이 하나도 없으면 → 허용(opt-in, 락아웃 방지).
 *   3) 하위 메뉴(업무유형 T, 모듈 M) {@link MenuGate.canType}: `type:T` 행이 있으면 그 actions, 없으면 모듈 키 행(MODULE_TO_MENU[M])
 *      actions(호환 폴백), 모듈이 미매핑이면 허용, 그 외 차단(allowlist).
 *   4) 상위 메뉴(모듈 M) {@link MenuGate.can}: M 소속 유형의 `type:` 행이 하나라도 있으면 그 행들의 actions 합집합,
 *      없으면 모듈 키 행 actions(호환 폴백), 모듈이 미매핑이면 허용. `type:` 행은 actions 가 비어도 "존재"로 보아 폴백을 막는다(꺼짐).
 *   5) 같은 targetId 행이 회사공통(workspaceId 없음)·현재 워크스페이스 스코프에 둘 다 있으면 워크스페이스 행만 쓴다
 *      (워크스페이스 행 우선 — 최종 리뷰 R-I3, 2026-10-21). 같은 스코프 안의 중복 행은 합집합. 3)·4)는 이 해석 결과로 판정.
 */
import type { Permission } from "@eqms/shared-types";

/** CRUD 액션 토큰(BE PERM_ACTIONS·PermissionCheckService 와 계약 일치). */
export const MENU_ACTION = {
  CREATE: "c",
  READ: "r",
  UPDATE: "u",
  DELETE: "d",
} as const;

/** 교육 관리 module_code — 교육 하위 화면(자격인증관리대장 등) 게이트 호출용. */
export const MODULE_TRN = "TRN";

/**
 * 업무유형 module_code(대문자) → 권한 메뉴 키(ADM_MENUS code). 사이드바 bucket.moduleCode 는 대문자임.
 * BE MODULE_TO_MENU 와 동일 매핑(미매핑 모듈은 게이트 대상 아님).
 */
export const MODULE_TO_MENU = new Map<string, string>([
  ["DOC", "documents"],
  ["CAPA", "capa"],
  ["CHG", "change"],
  ["CMP", "complaint"],
  ["NC", "nc"],
  ["AUD", "audit"],
  ["RSK", "risk"],
  [MODULE_TRN, "training"],
  ["DSN", "design"],
  ["PRODUCT", "products"],
  ["SUP", "supplier"],
]);

/** 추적성 메뉴 키(ADM_MENUS) — module_code 매핑이 없는 메뉴 직접 게이트용(BE MENU_TRACE 와 일치). */
export const MENU_TRACE = "trace";

/** 세부 메뉴(업무유형) 권한 키 접두사 — `type:<TYPE_CODE>`(BE TYPE_KEY_PREFIX 와 일치). */
export const TYPE_KEY_PREFIX = "type:";

/** 업무유형 코드 → 권한 키(`type:CODE`, 대문자 정규화). */
export function typeKey(typeCode: string): string {
  return `${TYPE_KEY_PREFIX}${typeCode.toUpperCase()}`;
}

/** 대시보드 하위 메뉴 권한 키 — 행이 없으면 허용, 있으면 조회(r) 필요. */
export const MENU_DASHBOARD_PERSONAL = "dashboard.personal";
export const MENU_DASHBOARD_OVERALL = "dashboard.overall";

/** 개인별 교육 이력 전사 조회(직원 목록) 메뉴 키(V140). 이 키의 조회 권한이 없으면 본인 이력만 본다. */
export const MENU_TRAINING_HISTORY = "training_history";

/** 업무유형 리프 바로 뒤에 부착되는 추가 권한 키(typeCode 대문자 → 메뉴 키). 권한 탭 트리가 이 표로 하위 리프를 붙임. */
export const TYPE_EXTRA_KEYS: ReadonlyMap<string, string> = new Map([
  ["TRN_RECORD", MENU_TRAINING_HISTORY],
]);

/**
 * menu 권한 행 → 키별 actions(워크스페이스 행 우선, R-I3). 키마다 현재 워크스페이스 행이 있으면 그 actions, 없으면 회사공통 행
 * actions. 같은 스코프 중복 행은 합집합. menu 외 targetType 은 무시. 게이트·설정 탭 초기 표시 공용(BE resolveByKey 와 일치).
 */
export function resolveMenuActions(perms: readonly Permission[]): Map<string, Set<string>> {
  const workspace = new Map<string, Set<string>>();
  const common = new Map<string, Set<string>>();
  for (const p of perms) {
    if (p.targetType !== "menu") continue;
    const scope = p.workspaceId ? workspace : common;
    const set = scope.get(p.targetId) ?? new Set<string>();
    for (const a of p.actions ?? []) set.add(a);
    scope.set(p.targetId, set);
  }
  return new Map([...common, ...workspace]); // 워크스페이스 행이 회사공통 행을 덮음
}

export interface MenuGate {
  /** 모듈(module_code) 판정(§5.2) — 소속 유형 `type:` 행 합집합, 없으면 모듈 키 폴백. */
  can(moduleCode: string, action: string): boolean;
  /** 모듈 조회(r) 가능 여부 — 사이드바 노출 판정. */
  canRead(moduleCode: string): boolean;
  /** 메뉴 키(ADM_MENUS code) 직접 판정 — module_code 매핑 없는 메뉴(예: trace)용. */
  canMenuKey(menuKey: string, action: string): boolean;
  /** 업무유형(하위 메뉴) 판정(§5.1) — `type:T` 행 우선, 없으면 모듈 키 폴백. */
  canType(typeCode: string, moduleCode: string, action: string): boolean;
  /** 대시보드 하위 메뉴 노출 — 행 없으면 허용, 있으면 조회(r) 포함 시에만. */
  canDashboard(key: typeof MENU_DASHBOARD_PERSONAL | typeof MENU_DASHBOARD_OVERALL): boolean;
}

/**
 * 현재 사용자 권한으로 메뉴 게이트를 구성함.
 *
 * @param perms 현재 사용자 권한 목록(GET /auth/me/permissions). undefined=로딩중(허용 폴백).
 * @param isAdmin 관리자 역할(CompanyAdmin/SuperAdmin) 여부.
 * @param typeModules 대문자 typeCode → 대문자 moduleCode(업무유형 목록). undefined=로딩중 —
 *   이때 `type:` 행이 하나라도 있으면 모듈 판정은 허용 폴백(서버가 최종 차단).
 */
export function makeMenuGate(
  perms: Permission[] | undefined,
  isAdmin: boolean,
  typeModules?: ReadonlyMap<string, string>,
): MenuGate {
  // 키별 스코프 해석(워크스페이스 행 우선, R-I3).
  const menuActions = resolveMenuActions(perms ?? []);
  // opt-in 판정은 menu 타입 권한만으로(BE 와 동일). workitem/field 권한이 추가돼도 menu 미설정=제한없음 유지.
  const hasAnyPerm = menuActions.size > 0;

  const canMenuKey = (menuKey: string, action: string): boolean => {
    if (isAdmin) return true;
    if (!hasAnyPerm) return true; // opt-in(권한 미설정 = 제한 없음)
    return menuActions.get(menuKey)?.has(action) ?? false; // allowlist
  };

  const canType = (typeCode: string, moduleCode: string, action: string): boolean => {
    if (isAdmin || !hasAnyPerm) return true;
    const own = menuActions.get(typeKey(typeCode));
    if (own !== undefined) return own.has(action); // 빈 actions = 꺼짐(폴백 차단)
    const menuKey = MODULE_TO_MENU.get(moduleCode.toUpperCase());
    if (menuKey === undefined) return true; // 게이트 대상 아님(미매핑 모듈)
    return menuActions.get(menuKey)?.has(action) ?? false;
  };

  const can = (moduleCode: string, action: string): boolean => {
    const module = moduleCode.toUpperCase();
    const menuKey = MODULE_TO_MENU.get(module);
    if (menuKey === undefined) return true; // 게이트 대상 아님(미매핑 모듈)
    if (isAdmin || !hasAnyPerm) return true;
    const typeRows: Set<string>[] = [];
    let anyTypeRow = false;
    for (const [id, actions] of menuActions) {
      if (!id.startsWith(TYPE_KEY_PREFIX)) continue;
      anyTypeRow = true;
      if (typeModules?.get(id.slice(TYPE_KEY_PREFIX.length).toUpperCase()) === module) {
        typeRows.push(actions);
      }
    }
    // 업무유형 목록 로딩 전: 유형→모듈 매핑을 모르므로 type: 행이 있으면 허용 폴백(서버가 최종 차단).
    if (typeModules === undefined && anyTypeRow) return true;
    if (typeRows.length > 0) return typeRows.some((s) => s.has(action));
    return menuActions.get(menuKey)?.has(action) ?? false; // 레거시 모듈 키 폴백
  };

  const canDashboard: MenuGate["canDashboard"] = (key) => {
    if (isAdmin) return true;
    const row = menuActions.get(key);
    return row === undefined || row.has(MENU_ACTION.READ); // 행 없음 = 허용(현행 동작 유지)
  };

  return {
    can,
    canRead: (moduleCode) => can(moduleCode, MENU_ACTION.READ),
    canMenuKey,
    canType,
    canDashboard,
  };
}
